import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import {
  Button,
  Field,
  Spinner,
  Textarea,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  latestPosReport, useChangeGlAccountCode, useClientGlMap, useGlAccountUsage, useReadPosReport, useUploads, useUpsertClientGlMap,
} from '../services/conversionsApi';
import type { ClientGlMap, ClientJournalTemplate, GlAccount, TemplateLine } from '../types';
import GlChartSection, { type GlListValue } from './GlChartSection';
import JournalTemplateSection, { type TemplateValue } from './JournalTemplateSection';
import { ownTemplate, startOwnTemplate } from '../lib/clientTemplate';
import { chartWithMappedCodes } from '../lib/glChart';
import { RETIRED_ROLES, oneListAccounts } from '../lib/glOneList';

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS },
});

type FormState = {
  /** Role (template or custom) → the client's Xero account code. */
  roles: Record<string, string>;
  notes: string;
  /** The client's chart (docs/client-chart-and-sales-split.md §1), with every mapped code in it (the one list). */
  accounts: GlAccount[];
  /** A client-defined line's label, by its custom key. */
  rowLabels: Record<string, string>;
  /** The client's own journal template, or null for the default (docs/client-journal-template.md §A). */
  journalTemplate: ClientJournalTemplate | null;
};

const emptyForm = (existing: ClientGlMap | null): FormState => {
  // The backend projects legacy typed properties into `roles` on read (GlMapService.GetAsync →
  // GlMapRoles.ProjectTypedIntoRoles), so a map saved before roles existed already arrives in this shape.
  const roles = { ...(existing?.roles ?? {}) };
  return {
    roles,
    notes: existing?.notes ?? '',
    // THE MIGRATION (one list, 2026-10-28): every code the old mapping rows named becomes a chart row here, so nothing
    // a bookkeeper mapped is out of sight. Saving writes it into the stored chart.
    accounts: oneListAccounts(existing?.accounts ?? [], roles),
    rowLabels: { ...(existing?.rowLabels ?? {}) },
    journalTemplate: ownTemplate(existing?.journalTemplate) ? { lines: existing!.journalTemplate!.lines.map((l) => ({ ...l })) } : null,
  };
};

/**
 * The per-client G/L account map, editable (CONVERSIONS_SPEC.md §5.4).
 *
 * <p>Extracted verbatim from `GlMappingPage` so the client page's G/L tab and the
 * standalone bulk-setup screen are ONE implementation. §8.2 keeps both surfaces on
 * purpose — the tab for "what is this client's mapping?", the standalone screen for bulk
 * setup — and two copies of an editable form over the same records would be a data-entry
 * hazard, not just duplication: they would drift, and staff would have no way to tell
 * which one they were looking at.</p>
 *
 * <p>The client is passed IN rather than picked here, so each host owns its own client
 * context (§9's one-client-per-page rule, carried in `?clientId=`).</p>
 *
 * <p>🗳 <b>ONE LIST (ruled by Christian, 2026-10-28):</b> the client's chart, each account with what it is USED AS
 * (`GlChartSection`). The role table that came before it (and the eight typed fields before THAT, which left five roles
 * with no input anywhere) is gone; every role is still reachable, from every account's "used as". The saved record is
 * unchanged, so the server and the templates read it as before.</p>
 */
export default function GlMapEditor({ clientId }: { clientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const activeAccount = accounts[0];

  const [form, setForm] = useState<FormState>(emptyForm(null));
  const [savedFlash, setSavedFlash] = useState(false);

  const { data: existing, isLoading, isError, error: loadError } = useClientGlMap(clientId);
  const upsert = useUpsertClientGlMap();
  const { data: usage } = useGlAccountUsage(clientId);
  const changeCode = useChangeGlAccountCode();
  // The client's latest POS report: what the template's preview and "Add lines from this report" read (wave 4 §3–§4).
  const latest = latestPosReport(useUploads(clientId).data);
  const reportRead = useReadPosReport(clientId, latest?.id);
  // Unsaved edits: a code change refreshes the map from the server, so it is offered only on a saved screen (§B3).
  const dirty = useMemo(
    () => existing !== undefined && JSON.stringify(form) !== JSON.stringify(emptyForm(existing)),
    [form, existing],
  );

  useEffect(() => {
    if (existing !== undefined) setForm(emptyForm(existing));
  }, [existing]);

  // Switching client must clear a stale "Saved" flash — otherwise it reads as confirmation
  // of a save against the client now on screen, which never happened.
  useEffect(() => {
    setSavedFlash(false);
  }, [clientId]);

  function handleSave() {
    if (!clientId) return;

    // Blank codes are omitted rather than sent empty, and that IS how a mapping is
    // cleared: the upsert deserializes into a fresh document and replaces the stored
    // one, so a role absent from this object is unset rather than left behind.
    const roles: Record<string, string> = {};
    for (const [role, code] of Object.entries(form.roles)) {
      const trimmed = code.trim();
      if (trimmed) roles[role] = trimmed;
    }

    upsert.mutate(
      {
        clientId,
        payload: {
          roles,
          // Always sent: absent keeps the stored chart, so leaving it out would make a cleared chart unclearable.
          // Every visible mapping's code is in it; a code only the retired role names stays in `roles` alone.
          accounts: chartWithMappedCodes(
            form.accounts,
            Object.entries(roles).filter(([role]) => !RETIRED_ROLES.has(role)).map(([, code]) => code),
          ),
          rowLabels: form.rowLabels,
          // Always sent: absent keeps the stored template, so "use the default" is an empty list.
          journalTemplate: form.journalTemplate ?? { lines: [] },
          notes: form.notes.trim() || undefined,
          updatedByName: activeAccount?.name ?? activeAccount?.username ?? undefined,
        },
      },
      {
        onSuccess: () => {
          setSavedFlash(true);
          setTimeout(() => setSavedFlash(false), 2500);
        },
      },
    );
  }

  if (!clientId) return null;
  if (isLoading) return <Spinner label={t('common.loading')} />;
  if (isError) {
    // The LOAD's own error. This read `upsert.error` until 2026-10-22 (CNV-010): the save mutation's error, which
    // is null while loading fails, so every load failure showed "Unknown error" instead of fetchApi's localized
    // reason.
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          {loadError instanceof Error ? loadError.message : t('common.unknownError')}
        </MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div className={styles.form}>
      {/* `null` is "no mapping saved" (the API answers 200 with data: null). Said, so a blank form is not read as a lost one. */}
      {existing === null && (
        <MessageBar intent="info">
          <MessageBarBody>{t('glMappingPage.noMapYet')}</MessageBarBody>
        </MessageBar>
      )}
      {savedFlash && (
        <MessageBar intent="success">
          <MessageBarBody>{t('glMappingPage.saveSuccess')}</MessageBarBody>
        </MessageBar>
      )}
      <GlChartSection
        value={{ accounts: form.accounts, roles: form.roles, rowLabels: form.rowLabels }}
        onChange={(next: GlListValue) => setForm((p) => ({ ...p, ...next }))}
        usage={usage}
        dirty={dirty}
        onChangeCode={async (from, to) => (await changeCode.mutateAsync({ clientId, from, to })).journalsChanged}
        onAddTemplateLine={(line: TemplateLine) =>
          // A line the template already has (the client's line of that name) is not added twice, nor moved.
          setForm((p) => (p.journalTemplate?.lines.some((l) => l.key === line.key) ? p : { ...p, journalTemplate: startOwnTemplate(p.journalTemplate, line) }))}
        onDefaultTemplate={form.journalTemplate === null}
      />
      <JournalTemplateSection
        value={{ journalTemplate: form.journalTemplate, accounts: form.accounts, roles: form.roles, rowLabels: form.rowLabels }}
        onChange={(next: TemplateValue) => setForm((p) => ({ ...p, ...next }))}
        report={{ fileName: latest?.fileName ?? null, read: reportRead.data, loading: reportRead.isLoading && !!latest, failed: reportRead.isError }}
      />
      <Field label={t('glMappingPage.fields.notes')}>
        <Textarea value={form.notes} onChange={(_e, data) => setForm((p) => ({ ...p, notes: data.value }))} rows={3} />
      </Field>
      <div className={styles.actions}>
        <Button
          appearance="primary"
          onClick={handleSave}
          disabled={upsert.isPending}
          icon={upsert.isPending ? <Spinner size="tiny" /> : undefined}
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
